import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Client,
  clientService,
  UpdateClientRequest,
  EfromClient,
  EclientStatus,
} from "@/services/client";
import {
  subscriptionService,
  Subscription,
  ESubscriptionStatus,
  ESubType,
  getSubscriptionServiceId,
  getSubscriptionStreamingAccount,
} from "@/services/subscription";
import {
  paymentService,
  Payment,
  EPaymentStatus,
  EPaymentMethod,
} from "@/services/payment";
import { ExtendSubscriptionModal } from "@/components/subscriptions/ExtendSubscriptionModal";
import { WhatsAppContactPicker } from "@/components/shared/WhatsAppContactPicker";
import { TransferSubscriptionModal } from "@/components/subscriptions/TransferSubscriptionModal";
import { WhatsAppChatSheet } from "@/components/whatsapp/chat/WhatsAppChatSheet";
import {
  ArrowLeft,
  Loader2,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Clock,
  DollarSign,
  FileText,
  Pencil,
  RefreshCw,
  ArrowRightLeft,
  Eye,
  MessageCircle,
  UserCheck,
  UserX,
  ChevronRight,
  Users,
  Wallet,
  CalendarClock,
  TrendingUp,
  Bell,
  BellOff,
} from "lucide-react";
import { calculateDaysRemaining, formatCurrency } from "@/lib/utils";

// Helper functions
const fromClientLabels: Record<EfromClient, string> = {
  [EfromClient.WEB]: "Web",
  [EfromClient.REFERRAL]: "Referido",
  [EfromClient.ADVERTISEMENT]: "Publicidad",
  [EfromClient.FACEBOOK]: "Facebook",
  [EfromClient.INSTAGRAM]: "Instagram",
  [EfromClient.OTHER]: "Otro",
};

function mapSubscriptionStatus(
  status: ESubscriptionStatus
): "ACTIVE" | "EXPIRING" | "EXPIRED" | "CANCELLED" {
  const statusMap: Record<
    ESubscriptionStatus,
    "ACTIVE" | "EXPIRING" | "EXPIRED" | "CANCELLED"
  > = {
    [ESubscriptionStatus.ACTIVE]: "ACTIVE",
    [ESubscriptionStatus.EXPIRING]: "EXPIRING",
    [ESubscriptionStatus.EXPIRED]: "EXPIRED",
    [ESubscriptionStatus.CANCELLED]: "CANCELLED",
  };
  return statusMap[status] || "ACTIVE";
}

function getPaymentStatusLabel(status: EPaymentStatus): string {
  const labels: Record<EPaymentStatus, string> = {
    [EPaymentStatus.COMPLETED]: "Completado",
    [EPaymentStatus.PENDING]: "Pendiente",
    [EPaymentStatus.CANCELLED]: "Cancelado",
    [EPaymentStatus.REFUNDED]: "Reembolsado",
  };
  return labels[status] || status;
}

function getPaymentStatusColor(status: EPaymentStatus): string {
  const colors: Record<EPaymentStatus, string> = {
    [EPaymentStatus.COMPLETED]: "bg-green-100 text-green-800",
    [EPaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800",
    [EPaymentStatus.CANCELLED]: "bg-red-100 text-red-800",
    [EPaymentStatus.REFUNDED]: "bg-blue-100 text-blue-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
}

function getPaymentMethodLabel(method: EPaymentMethod): string {
  const labels: Record<EPaymentMethod, string> = {
    [EPaymentMethod.CASH]: "Efectivo",
    [EPaymentMethod.TRANSFER]: "Transferencia",
    [EPaymentMethod.NEQUI]: "Nequi",
    [EPaymentMethod.DAVIPLATA]: "Daviplata",
    [EPaymentMethod.BANCOLOMBIA]: "Bancolombia",
    [EPaymentMethod.PAYPAL]: "PayPal",
    [EPaymentMethod.BINANCE]: "Binance",
    [EPaymentMethod.YAPE]: "Yape",
  };
  return labels[method] || method;
}

export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);

  // Form state for editing
  const [editFormData, setEditFormData] = useState<UpdateClientRequest>({
    name: "",
    phone: "",
    email: "",
    notes: "",
    status: EclientStatus.ACTIVE,
    from: EfromClient.WEB,
    notify: true,
  });

  // Query for client
  const {
    data: clientResponse,
    isLoading: isLoadingClient,
    error: clientError,
  } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientService.getOne(clientId!),
    enabled: !!clientId,
  });

  const client = clientResponse?.data;

  // Query for subscriptions
  // const { data: subscriptionsResponse, isLoading: isLoadingSubscriptions } =
  //   useQuery({
  //     queryKey: ["subscriptions", "client", clientId],
  //     queryFn: () => subscriptionService.getByClient(clientId!),
  //     enabled: !!clientId,
  //   });

  // Query for payments
  // const { data: paymentsResponse, isLoading: isLoadingPayments } = useQuery({
  //   queryKey: ["payments", "client", clientId],
  //   queryFn: () => paymentService.getByClient(clientId!),
  //   enabled: !!clientId,
  // });

  const subscriptions = clientResponse?.data?.subscriptions || [];
  const payments = clientResponse?.data?.subscriptions.flatMap((s)=>s.payments) || []

  // Mutation for updating client
  const updateMutation = useMutation({
    mutationFn: (data: UpdateClientRequest) =>
      clientService.update(clientId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setIsEditDialogOpen(false);
      toast.success("Cliente actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Mutation for toggling status
  const toggleStatusMutation = useMutation({
    mutationFn: () =>
      clientService.update(clientId!, {
        status:
          client?.status === EclientStatus.ACTIVE
            ? EclientStatus.INACTIVE
            : EclientStatus.ACTIVE,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success(
        `Cliente ${client?.status === EclientStatus.ACTIVE ? "desactivado" : "activado"} exitosamente`
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Handlers
  const handleEdit = () => {
    if (client) {
      setEditFormData({
        name: client.name,
        phone: client.phone,
        email: client.email,
        notes: client.notes || "",
        status: client.status,
        from: client.clientFrom,
        notify: client.notify,
      });
      setIsEditDialogOpen(true);
    }
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(editFormData);
  };

  const handleEditChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleExtendSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsExtendModalOpen(true);
  };

  const handleTransferSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setIsTransferModalOpen(true);
  };


  const isLoading = isLoadingClient ;

  // Calculate stats
  const activeSubscriptions = subscriptions.filter(
    (s) =>
      s.status === ESubscriptionStatus.ACTIVE ||
      s.status === ESubscriptionStatus.EXPIRING
  );

  const totalPaid = payments
    .filter((p) => p.status === EPaymentStatus.COMPLETED)
    .reduce((sum, p) => Number(sum) + Number(p.amount), 0);

  const nextExpiring = activeSubscriptions.reduce(
    (min, s) => {
      if (!min || calculateDaysRemaining(new Date(s.endDate)) < calculateDaysRemaining(new Date(min.endDate))) return s;
      return min;
    },
    null as Subscription | null
  );

  if (isLoading) {
    return (
      <DashboardLayout
        title="Detalle de Cliente"
        subtitle="Cargando informacion..."
      >
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Cargando...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (clientError || !client) {
    return (
      <DashboardLayout title="Detalle de Cliente" subtitle="Error">
        <div className="flex flex-col items-center justify-center p-12 gap-4">
          <p className="text-destructive">No se pudo cargar el cliente</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Custom header
  const customHeader = (
    <div className="space-y-4">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/dashboard" className="hover:text-foreground transition-colors">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/clients" className="hover:text-foreground transition-colors">
          Clientes
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground">{client.name}</span>
      </nav>

      {/* Header content */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/clients")}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-bold text-xl">
              {client.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">
                {client.name}
              </h1>
              <StatusBadge status={client.status} />
            </div>
            <p className="text-muted-foreground text-sm">
              Cliente desde{" "}
              {new Date(client.createdAt).toLocaleDateString("es-CO", {
                year: "numeric",
                month: "long",
              })}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 ml-auto sm:ml-0">
          <Button variant="outline" size="sm" onClick={handleEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-success hover:text-success"
            onClick={() => setIsChatOpen(true)}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Chat
          </Button>
          <Button
            variant={
              client.status === EclientStatus.ACTIVE ? "destructive" : "default"
            }
            size="sm"
            onClick={() => toggleStatusMutation.mutate()}
            disabled={toggleStatusMutation.isPending}
          >
            {toggleStatusMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : client.status === EclientStatus.ACTIVE ? (
              <UserX className="h-4 w-4 mr-2" />
            ) : (
              <UserCheck className="h-4 w-4 mr-2" />
            )}
            {client.status === EclientStatus.ACTIVE ? "Desactivar" : "Activar"}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout customHeader={customHeader} title="Detalle de Cliente">
      <div className="space-y-6 animate-fade-in">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Suscripciones Activas
                  </p>
                  <p className="text-2xl font-bold">{activeSubscriptions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/10">
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Mensual</p>
                  <p className="text-2xl font-bold">
                    ${formatCurrency(Number(client.monthlyValue))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/10">
                  <Wallet className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Pagado</p>
                  <p className="text-2xl font-bold">
                    ${formatCurrency(totalPaid)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-orange-500/10">
                  <CalendarClock className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Proximo Vencimiento
                  </p>
                  <p className="text-2xl font-bold">
                    {nextExpiring
                      ? nextExpiring.daysRemaining > 0
                        ? `${nextExpiring.daysRemaining} dias`
                        : "Vencido"
                      : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Client Info Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              Informacion del Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Telefono</p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{client.phone}</p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-success"
                      onClick={() => setIsChatOpen(true)}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{client.email || "-"}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Origen</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    {fromClientLabels[client.clientFrom]}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Fecha de Registro
                  </p>
                  <p className="font-medium">
                    {new Date(client.createdAt).toLocaleDateString("es-CO")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                {client.notify ? (
                  <Bell className="h-5 w-5 text-muted-foreground mt-0.5" />
                ) : (
                  <BellOff className="h-5 w-5 text-muted-foreground mt-0.5" />
                )}
                <div>
                  <p className="text-sm text-muted-foreground">
                    Notificaciones automáticas
                  </p>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      client.notify
                        ? "bg-success/10 text-success"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {client.notify ? "Activas" : "Inactivas"}
                  </span>
                </div>
              </div>

              {client.notes && (
                <div className="flex items-start gap-3 md:col-span-2">
                  <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Notas</p>
                    <p className="font-medium">{client.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscriptions Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5 text-primary" />
                Suscripciones
              </CardTitle>
              <span className="text-sm text-muted-foreground">
                {subscriptions.length} suscripcion(es)
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {subscriptions.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No hay suscripciones</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Este cliente no tiene suscripciones activas
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Servicio</TableHead>
                      <TableHead>Cuenta / Slot</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Fin</TableHead>
                      <TableHead className="text-center">Dias Restantes</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>
                          {subscription.type === ESubType.COMBO ? (
                            <ServiceBadge icon="📦" name={subscription?.combo?.name || "Combo"} />
                          ) : (
                            <ServiceBadge icon={subscription.subscriptionItems[0]?.service?.icon|| "—"} name={subscription.subscriptionItems[0]?.service?.name|| "—"} />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">
                              {subscription.subscriptionItems?.[0]?.slot?.name || "Cuenta completa"}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {subscription.type === ESubType.COMBO
                                ? "Múltiples cuentas"
                                : subscription.subscriptionItems?.[0]?.streamingAccount?.email || "-"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(subscription.startDate).toLocaleDateString(
                            "es-CO"
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(subscription.endDate).toLocaleDateString(
                            "es-CO"
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`font-semibold ${
                              subscription.daysRemaining <= 3
                                ? "text-destructive"
                                : subscription.daysRemaining <= 7
                                  ? "text-orange-500"
                                  : "text-foreground"
                            }`}
                          >
                            {subscription.daysRemaining > 0
                              ? subscription.daysRemaining
                              : "Vencido"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${formatCurrency(subscription.price)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={mapSubscriptionStatus(subscription.status)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                navigate(`/subscriptions/${subscription.id}`)
                              }
                              title="Ver detalle"
                            >
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            {subscription.status !==
                              ESubscriptionStatus.CANCELLED && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    handleExtendSubscription(subscription)
                                  }
                                  title="Extender"
                                >
                                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    handleTransferSubscription(subscription)
                                  }
                                  title="Transferir"
                                  disabled={subscription.type === ESubType.COMBO}
                                >
                                  <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-primary" />
                Historial de Pagos
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                Total pagado:{" "}
                <span className="font-semibold text-foreground">
                  ${formatCurrency(totalPaid)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No hay pagos registrados</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Los pagos apareceran cuando se registren suscripciones
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Metodo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Notas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {new Date(payment.paymentDate).toLocaleDateString(
                            "es-CO"
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          ${formatCurrency(Number(payment.amount))}
                        </TableCell>
                        <TableCell>
                          {getPaymentMethodLabel(payment.paymentMethod)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}
                          >
                            {getPaymentStatusLabel(payment.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {payment.notes || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Client Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>
              Actualiza la informacion del cliente
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nombre</Label>
              <div className="flex gap-2">
                <Input
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditChange}
                  placeholder="John Doe"
                  disabled={updateMutation.isPending}
                  className="flex-1"
                />
                <WhatsAppContactPicker
                  disabled={updateMutation.isPending}
                  onSelect={({ name, phone }) =>
                    setEditFormData((prev) => ({ ...prev, name, phone }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">Telefono</Label>
              <Input
                id="edit-phone"
                name="phone"
                value={editFormData.phone}
                onChange={handleEditChange}
                placeholder="+1 555-0123"
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                name="email"
                type="email"
                value={editFormData.email}
                onChange={handleEditChange}
                placeholder="john@example.com"
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-status">Estado</Label>
              <select
                id="edit-status"
                name="status"
                value={editFormData.status}
                onChange={handleEditChange}
                disabled={updateMutation.isPending}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="ACTIVE">Activo</option>
                <option value="INACTIVE">Inactivo</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-from">Origen</Label>
              <select
                id="edit-from"
                name="from"
                value={editFormData.from}
                onChange={handleEditChange}
                disabled={updateMutation.isPending}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {Object.values(EfromClient).map((value) => (
                  <option key={value} value={value}>
                    {fromClientLabels[value]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notas</Label>
              <Textarea
                id="edit-notes"
                name="notes"
                value={editFormData.notes}
                onChange={handleEditChange}
                placeholder="Notas adicionales sobre el cliente..."
                disabled={updateMutation.isPending}
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div>
                <Label htmlFor="edit-notify" className="text-sm">
                  Notificaciones automáticas
                </Label>
                <p className="text-xs text-muted-foreground">
                  Permite que las automatizaciones notifiquen a este cliente.
                </p>
              </div>
              <Switch
                id="edit-notify"
                checked={editFormData.notify}
                onCheckedChange={(checked) =>
                  setEditFormData((prev) => ({ ...prev, notify: checked }))
                }
                disabled={updateMutation.isPending}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                disabled={updateMutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Extend Subscription Modal */}
      <ExtendSubscriptionModal
        open={isExtendModalOpen}
        onOpenChange={setIsExtendModalOpen}
        subscription={selectedSubscription}
      />

      {/* Transfer Subscription Modal */}
      {selectedSubscription && (
        <TransferSubscriptionModal
          open={isTransferModalOpen}
          onOpenChange={setIsTransferModalOpen}
          subscriptionId={String(selectedSubscription.subscriptionItems?.[0]?.id)} // todo validate subItemId
          currentSlotName={selectedSubscription.subscriptionItems?.[0]?.slot?.name || "Cuenta completa"}
          currentAccountEmail={getSubscriptionStreamingAccount(selectedSubscription)?.email || ""}
          clientName={client.name}
          serviceId={getSubscriptionServiceId(selectedSubscription) ?? 0}
        />
      )}

      <WhatsAppChatSheet
        open={isChatOpen}
        onOpenChange={setIsChatOpen}
        phone={client.phone}
        name={client.name}
      />
    </DashboardLayout>
  );
}
